import React from "react";
import { motion } from "framer-motion";
import { FileText, Download, CheckCircle2, Circle, Clock } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal, Stagger, staggerItem } from "@/components/motion/Reveal";
import { PlatformCta } from "@/components/sections/PlatformCta";
import { SectionHeading } from "@/components/ui/primitives";
import { useLang } from "@/i18n/LanguageContext";
import { deepLocalize } from "@/i18n/translate";

const papers = [
  {
    title: "Verifiable AI Governance: A Reference Framework",
    tag: "White Paper",
    text: "A reference framework for translating regulatory obligations into repeatable, evidence-backed controls.",
  },
  {
    title: "The AI Bill of Materials (AI-BOM) Specification",
    tag: "Technical Publication",
    text: "A structured specification for inventorying models, datasets, dependencies and controls.",
  },
  {
    title: "European Digital Sovereignty & Sovereign AI",
    tag: "Position Paper",
    text: "Why critical sectors require AI infrastructure aligned with European values and jurisdiction.",
  },
  {
    title: "EU AI Act Readiness: An Operational Playbook",
    tag: "Guide",
    text: "From risk classification to evidence — an operational approach to AI Act readiness.",
  },
];

const roadmap = [
  { status: "done", period: "2025", title: "Foundation", text: "AI inventory, risk register, lifecycle and audit centre established." },
  { status: "active", period: "2026 · Now", title: "Private Beta", text: "Governance OS private beta for governments and critical infrastructure." },
  { status: "next", period: "2026 H2", title: "Framework Expansion", text: "Additional regulatory profiles and controls libraries." },
  { status: "next", period: "2027", title: "Platform Ecosystem", text: "Academy, Research Hub, Developers Portal and Partner Network." },
];

const statusIcon = { done: CheckCircle2, active: Clock, next: Circle };

export default function Research() {
  const { lang, tr } = useLang();
  const pubs = deepLocalize(papers, lang);
  const plan = deepLocalize(roadmap, lang);
  return (
    <>
      <Seo
        title={tr("Research, White Papers & Roadmap")}
        description={tr("AstroLife research, white papers and publications on verifiable AI governance, the EU AI Act, digital sovereignty and trustworthy AI — plus our platform roadmap.")}
        path="/research"
        keywords="AI Governance research, White Papers, EU AI Act, Verifiable AI, Responsible AI, AI Audit, Roadmap"
      />

      <PageHero
        overline={tr("Research & Publications")}
        title={tr("Evidence-based research on verifiable AI governance.")}
        subtitle={tr("White papers, technical publications and position papers advancing trustworthy, auditable and sovereign artificial intelligence.")}
        breadcrumbs={[{ label: tr("Research") }]}
      >
        <div className="mt-8">
          <a href="/publications" className="inline-flex items-center gap-2 rounded-full bg-astro-blue px-7 py-3.5 font-semibold text-white hover:bg-astro-light transition-colors" data-testid="research-to-publications">
            {tr("Visit the Publication Center →")}
          </a>
        </div>
      </PageHero>

      {/* White papers */}
      <section className="bg-navy py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline={tr("White Papers")} title={tr("Publications & technical papers")} />
          <Stagger className="mt-14 grid gap-5 md:grid-cols-2">
            {pubs.map((p) => (
              <motion.article
                key={p.title}
                variants={staggerItem}
                className="group flex gap-5 rounded-2xl border border-navy-border bg-navy-surface p-7 transition-colors hover:border-astro-blue/40"
              >
                <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-astro-blue/10 text-astro-light">
                  <FileText className="h-6 w-6" />
                </div>
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-astro-light">{p.tag}</span>
                  <h3 className="mt-2 font-heading text-lg font-semibold text-white">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-silver/60">{p.text}</p>
                  <button
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-astro-light hover:text-white transition-colors"
                    data-testid={`paper-request-${p.title.slice(0, 10)}`}
                  >
                    <Download className="h-4 w-4" /> {tr("Request access")}
                  </button>
                </div>
              </motion.article>
            ))}
          </Stagger>
        </div>
      </section>

      <PlatformCta />

      {/* Roadmap */}
      <section className="border-t border-navy-border bg-navy py-24 lg:py-32" id="roadmap">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline={tr("Roadmap")} title={tr("Building the AstroLife platform ecosystem.")} />
          <div className="mt-14 space-y-4">
            {plan.map((r, i) => {
              const Icon = statusIcon[r.status];
              return (
                <Reveal key={r.title} delay={i * 0.05}>
                  <div className={`flex items-start gap-5 rounded-2xl border p-7 ${r.status === "active" ? "border-astro-blue/50 bg-astro-blue/5" : "border-navy-border bg-navy-surface"}`}>
                    <Icon className={`mt-0.5 h-6 w-6 flex-shrink-0 ${r.status === "done" ? "text-astro-light" : r.status === "active" ? "text-astro-light" : "text-silver/40"}`} />
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.15em] text-astro-light">{r.period}</p>
                      <h3 className="mt-1 font-heading text-xl font-semibold text-white">{r.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-silver/60">{r.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
